import { useState, useEffect, useCallback } from 'react';

interface UseForecastNavigationReturn {
  selectedDayIndex: number;
  setSelectedDayIndex: (index: number) => void;
  handlePreviousDay: () => void;
  handleNextDay: () => void;
  canGoPrevious: boolean;
  canGoNext: boolean;
  currentDate: string | null;
  formatDate: (dateStr: string) => string;
}

/**
 * Custom hook for managing 7-day forecast navigation state
 */
export function useForecastNavigation(forecastDates: string[]): UseForecastNavigationReturn {
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  // Reset to first day when dates change (e.g., new location)
  useEffect(() => {
    setSelectedDayIndex(0);
  }, [forecastDates.length]);

  const handlePreviousDay = useCallback(() => {
    setSelectedDayIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNextDay = useCallback(() => {
    setSelectedDayIndex((prev) => Math.min(forecastDates.length - 1, prev + 1));
  }, [forecastDates.length]);

  const canGoPrevious = selectedDayIndex > 0;
  const canGoNext = selectedDayIndex < forecastDates.length - 1;

  const currentDate = forecastDates[selectedDayIndex] || null;

  const formatDate = useCallback((dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    }
  }, []);

  return {
    selectedDayIndex,
    setSelectedDayIndex,
    handlePreviousDay,
    handleNextDay,
    canGoPrevious,
    canGoNext,
    currentDate,
    formatDate,
  };
}
